import type {
  AdminTenantUsage,
  AdminUsageOverview,
  PaginatedData,
  UsageTenantRow,
} from '@libs/types';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@api';
import { queryKeys } from '@constants';

import { usageEndpoints } from './admin-usage-endpoints';

/**
 * Platform-admin usage & cost API.
 *
 * A sibling of `admin-api.ts` rather than more of it: that file is already ~770
 * lines and the biggest merge-conflict magnet in the feature. This is a
 * self-contained slice, and keeping it separate keeps both diffs reviewable.
 */

export interface UsageRangeParams {
  preset?: string;
  from?: string;
  to?: string;
}

export interface UsageTenantsParams extends UsageRangeParams {
  page?: number;
  limit?: number;
}

/**
 * Cost over a past window is immutable, and even "today" only moves as the AI
 * service writes events. Refetching five grouped aggregates plus a Keycloak
 * directory read on every tab focus would be a self-inflicted load problem, and
 * cost is not a live metric — so this is cached and never polled.
 */
const COST_STALE_TIME_MS = 60_000;

const fetchUsageOverview = async (params: UsageRangeParams): Promise<AdminUsageOverview> => {
  const { data } = await apiClient.get<AdminUsageOverview>(usageEndpoints.overview(), { params });
  return data;
};

const fetchUsageTenants = async (
  params: UsageTenantsParams,
): Promise<PaginatedData<UsageTenantRow[]>> => {
  const { data } = await apiClient.get<PaginatedData<UsageTenantRow[]>>(
    usageEndpoints.tenants(),
    { params },
  );
  return data;
};

const fetchTenantUsage = async (
  tenantId: string,
  params: UsageRangeParams,
): Promise<AdminTenantUsage> => {
  const { data } = await apiClient.get<AdminTenantUsage>(
    usageEndpoints.tenantUsage(tenantId),
    { params },
  );
  return data;
};

// ── Queries ──────────────────────────────────────────────────────────────────

export const useAdminUsageOverview = (params: UsageRangeParams) =>
  useQuery({
    // The range MUST be part of the key, or changing the date silently serves
    // the previous window's numbers.
    queryKey: queryKeys.admin.usageOverview(params),
    queryFn: () => fetchUsageOverview(params),
    staleTime: COST_STALE_TIME_MS,
  });

export const useAdminUsageTenants = (params: UsageTenantsParams) =>
  useQuery({
    queryKey: queryKeys.admin.usageTenants(params),
    queryFn: () => fetchUsageTenants(params),
    staleTime: COST_STALE_TIME_MS,
  });

export const useAdminTenantUsage = (tenantId: string | undefined, params: UsageRangeParams) =>
  useQuery({
    queryKey: queryKeys.admin.tenantUsage(tenantId ?? '', params),
    queryFn: () => fetchTenantUsage(tenantId as string, params),
    enabled: Boolean(tenantId),
    staleTime: COST_STALE_TIME_MS,
  });
